<template>
<div class="tc-legend" v-bind:style="calc_style">
    <!-- OHLCV header -->
    <div v-if="grid_id === 0" class="tc-ohlcv"
        :style="{ 'max-width': common.width + 'px' }">
        <span v-html="common.icon" width="14" height="14"></span>
        <span class="t-vue-title">{{common.title_txt}}</span>
        <span class="t-vue-ohlcv" v-if="show_values">
            <template v-if="!compactOhlc">O<span class="t-vue-lspan">{{ohlcv[0]}}</span>
            H<span class="t-vue-lspan">{{ohlcv[1]}}</span>
            L<span class="t-vue-lspan">{{ohlcv[2]}}</span>
            </template>C<span class="t-vue-lspan">{{ohlcv[3]}}</span>
            <template v-if="!compactOhlc">V<span class="t-vue-lspan">{{ohlcv[4]}}</span></template>
            <strong class="tc-candle-change">{{ candleChange }}</strong>
            <button @click.stop="compactOhlc=!compactOhlc" :aria-pressed="compactOhlc" aria-label="Compact OHLC" title="Toggle compact / detailed OHLC">{{ compactOhlc ? '…' : '−' }}</button>
        </span>
        <span v-if="!show_values" class="t-vue-lspan"
            :style="{color: common.colors.text}">
            {{(common.meta.last || [])[4]}}
        </span>
    </div>

    <!-- Indicator groups -->
    <div class="t-vue-grp-ind" v-for="(grp, gname) in indicators" :key="gname">
        <!-- Group header -->
        <div v-if="grp.length > 1 || custom_groups.includes(gname)" class="t-vue-grp-header">
            <button type="button" :class="['t-vue-grp-chevron', { 'collapsed': !is_group_visible(gname) }]" :aria-label="'Toggle ' + gname" :aria-expanded="is_group_visible(gname)" @click.stop="toggle_group(gname)">▸</button>
            <span class="t-vue-grp">
                <span class="t-vue-gname">{{gname}}</span>
                <span class="t-vue-grp-count">({{grp.length}})</span>
                <span class="t-vue-grp-values" v-if="show_values && group_values(grp).length && has_valid_group_values(grp)">
                    <span class="t-vue-lspan t-vue-ivalue"
                        v-for="(v, i) in group_values(grp)" :key="i"
                        :style="{ color: v.color }">
                        {{v.value}}
                    </span>
                </span>
            </span>
            <span class="t-vue-grp-actions">
                <button class="t-vue-grp-btn" @click.stop="toggle_group_visibility(gname)" :title="is_all_visible(gname) ? 'Hide all' : 'Show all'">
                    <svg v-if="is_all_visible(gname)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
                <button class="t-vue-grp-btn t-vue-grp-del" @click.stop="delete_group_inline(gname)" title="Delete group">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                </button>
                <button class="t-vue-grp-btn" @click.stop="open_group_settings(gname, $event)" title="Group settings">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                </button>
                <button class="t-vue-grp-btn" @click.stop="open_indicator_popup(gname)" title="Edit group">
                    ⋯
                </button>
            </span>
        </div>

        <!-- Indicators -->
        <span v-if="is_group_visible(gname) || grp.length <= 1">
            <div class="t-vue-ind" v-for="ind in grp" :key="ind.id">
                <span class="t-vue-iname" :class="{'t-vue-iname-inactive': !ind.v}">{{ind.name}}</span>
                <span class="t-vue-ind-actions">
                    <button-group
                        v-bind:buttons="common.buttons"
                        v-bind:config="common.config"
                        v-bind:ov_id="ind.id"
                        v-bind:grid_id="grid_id"
                        v-bind:index="ind.index"
                        v-bind:tv_id="common.tv_id"
                        v-bind:display="ind.v"
                        v-on:legend-button-click="button_click">
                    </button-group>
                </span>
                <span class="t-vue-ivalues" v-if="ind.v && show_values && has_valid_values(ind.values)">
                    <span class="t-vue-lspan t-vue-ivalue"
                        v-for="(v, i) in ind.values" :key="i"
                        :style="{ color: v.color }">
                        {{v.value}}
                    </span>
                </span>
                <span v-if="ind.unk" class="t-vue-unknown">(Unknown type)</span>
                <transition name="tcjs-appear">
                    <spinner :colors="common.colors" v-if="ind.loading"></spinner>
                </transition>
            </div>
        </span>
    </div>

    <!-- New group button -->
    <div v-if="has_indicators" class="t-vue-new-grp" @click="create_group">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        <span>New Group</span>
    </div>

    <!-- Group popover -->
    <group-popover
        :visible="popover.visible"
        :group-name="popover.groupName"
        :items="popover.items"
        :all-groups="all_group_names"
        :all-visible="popover.allVisible"
        :anchor-x="popover.x"
        :anchor-y="popover.y"
        @close="close_popover"
        @rename="on_rename"
        @delete-group="on_delete_group"
        @toggle-visibility="on_toggle_visibility"
        @move-indicator="on_move_indicator"
    />
</div>
</template>

<script>
import ToggleButton from './ToggleButton.vue'
import ChartButton from './ChartButton.vue'
import ButtonGroup from './ButtonGroup.vue'
import Spinner from './Spinner.vue'
import GroupPopover from './GroupPopover.vue'

export default {
    name: 'ChartLegend',
    props: ['common', 'values', 'grid_id', 'meta_props'],
    components: { ButtonGroup, ToggleButton, ChartButton, Spinner, GroupPopover },
    computed: {
        candleChange() {
            const bar = this.values?.ohlcv || this.common.meta.last;
            if (!bar || !Number(bar[1])) return '';
            const value = (bar[4]-bar[1])/bar[1]*100;
            return (value>=0?'+':'')+value.toFixed(2)+'%';
        },
        ohlcv() {
            const bar = this.values?.ohlcv || this.common.meta.last;
            if (!bar) return Array(6).fill('');
            const prec = this.layout.prec
            let id = this.main_type + '_0'
            let meta = this.$props.meta_props[id] || {}
            if (meta.legend) return (meta.legend() || []).map(x => x.value)
            return [
                bar[1].toFixed(prec),
                bar[2].toFixed(prec),
                bar[3].toFixed(prec),
                bar[4].toFixed(prec),
                bar[5] ?
                    bar[5].toFixed(2) : ''
            ]
        },
        indicators() {
            const values = this.$props.values
            const f = this.format
            var types = {}
            var data = this.json_data.filter(
                x => x.settings.legend !== false && !x.main
            ).map(x => {
                if (!(x.type in types)) types[x.type] = 0
                const id = x.type + `_${types[x.type]++}`
                return {
                    v: 'display' in x.settings ? x.settings.display : true,
                    name: x.name || id,
                    group: x.settings.group || x.group || 'Indicators',
                    index: (this.off_data || this.json_data).indexOf(x),
                    id: id,
                    values: values ? f(id, values, x.settings) : this.n_a(1),
                    unk: !(id in (this.$props.meta_props || {})),
                    loading: this.is_loading(x),
                    _ov: x
                }
            })
            var grouped = {}
            for (var d of data) {
                if (!grouped[d.group]) grouped[d.group] = []
                grouped[d.group].push(d)
            }
            return grouped
        },
        has_indicators() {
            return Object.keys(this.indicators).length > 0
        },
        all_group_names() {
            let names = Object.keys(this.indicators)
            for (let g of this.custom_groups) {
                if (!names.includes(g)) names.push(g)
            }
            return names.sort()
        },
        calc_style() {
            let top = this.layout.height > 150 ? 10 : 5
            let grids = this.$props.common.layout.grids
            let w = grids[0] ? grids[0].width : undefined
            return {
                top: `${this.layout.offset + top}px`,
                width: `${w - 20}px`
            }
        },
        layout() {
            return this.$props.common.layout.grids[this.$props.grid_id]
        },
        json_data() { return this.$props.common.data },
        off_data() { return this.$props.common.offchart },
        main_type() {
            let f = this.common.data.find(x => x.main)
            return f ? f.type : undefined
        },
        show_values() {
            return this.common.cursor.mode !== 'explore'
        }
    },
    data() {
        return {
            compactOhlc: false,
            group_visible: {},
            custom_groups: JSON.parse(
                localStorage.getItem('tc_indicator_groups') || '[]'
            ),
            popover: {
                visible: false,
                groupName: '',
                items: [],
                allVisible: true,
                x: 0, y: 0
            }
        }
    },
    methods: {
        format(id, values, settings = {}) {
            let meta = this.$props.meta_props[id] || {}
            if (!values[id]) return this.n_a(1)
            const precision=Number(settings.precision);
            if (meta.legend) {
                const entries=meta.legend(values[id]);
                if(!Number.isInteger(precision)||precision<0||precision>16)return entries;
                return (entries||[]).map(entry=>({...entry,value:Number.isFinite(Number(entry.value))?Number(entry.value).toFixed(precision):entry.value}));
            }
            return values[id].slice(1).map((x, i) => {
                const cs = meta.data_colors ? meta.data_colors() : []
                if (typeof x == 'number') {
                    x = Number.isFinite(x) ? x.toFixed(Number.isInteger(precision)&&precision>=0&&precision<=16?precision:Math.abs(x)>0.001?4:8) : ''
                }
                return {
                    value: Number.isInteger(precision)&&precision>=0&&precision<=16?String(x):this.format_value(x),
                    color: cs ? cs[i % cs.length] : undefined
                }
            })
        },
        format_value(value) {
            if (value === undefined || value === null || value === '') return ''
            let text = String(value)
            if (text.length > 13) text = text.slice(0, 12) + '…'
            return text
        },
        group_values(grp) {
            let values = []
            for (let ind of grp || []) {
                if (!ind.v || !ind.values) continue
                for (let v of ind.values) {
                    if (!v || v.value === '' || v.value === 'n/a') continue
                    values.push(v)
                    if (values.length >= 4) return values
                }
            }
            return values
        },
        n_a(len) { return Array(len).fill({ value: '' }) },
        is_loading(overlay) {
            if (!overlay || !overlay.loading) return false
            if (!overlay.loadingStartedAt) return true
            return Date.now() - overlay.loadingStartedAt < 12000
        },
        button_click(event) {
            this.$emit('legend-button-click', event)
        },
        toggle_group(gname) {
            this.$set(this.group_visible, gname,
                !this.is_group_visible(gname))
        },
        is_group_visible(gname) {
            // Default to collapsed (false) if not explicitly set
            return this.group_visible[gname] === true
        },
        is_all_visible(gname) {
            let grp = this.indicators[gname]
            return grp && grp.every(ind => ind.v)
        },
        toggle_group_visibility(gname) {
            let grp = this.indicators[gname]
            if (!grp) return
            let type = this.grid_id === 0 ? 'onchart' : 'offchart'
            const visible = !this.is_all_visible(gname)
            for (let ind of grp) {
                if (ind.v === visible) continue
                this.$emit('legend-button-click', {
                    button: 'display', type: type, dataIndex: ind.index
                })
            }
        },
        delete_group_inline(gname) {
            let grp = this.indicators[gname]
            if (!grp || !grp.length) {
                let ci = this.custom_groups.indexOf(gname)
                if (ci >= 0) { this.custom_groups.splice(ci, 1); this.save_groups() }
                return
            }
            let indices = grp.map(ind => ind.index).sort((a, b) => b - a)
            let type = this.grid_id === 0 ? 'onchart' : 'offchart'
            for (let idx of indices) {
                this.$emit('legend-button-click', {
                    button: 'remove', type: type, dataIndex: idx
                })
            }
            let ci = this.custom_groups.indexOf(gname)
            if (ci >= 0) { this.custom_groups.splice(ci, 1); this.save_groups() }
        },

        // --- Popover ---
        open_popover(e, gname) {
            let rect = e.target.getBoundingClientRect()
            let grp = this.indicators[gname] || []
            this.popover = {
                visible: true,
                groupName: gname,
                items: grp,
                allVisible: grp.every(ind => ind.v),
                x: Math.min(rect.right + 4, window.innerWidth - 300),
                y: Math.min(rect.top, window.innerHeight - 350)
            }
        },

        open_indicator_popup(gname) {
            // Open the indicator popup (AddWin) via the same mechanism
            // as the "+" button on the legend
            this.$emit('legend-button-click', {
                button: 'add', type: 'onchart', dataIndex: 0
            })
        },
        open_group_settings(gname, event) {
            let grp = this.indicators[gname]
            if (!grp || !grp.length) return
            let type = this.grid_id === 0 ? 'onchart' : 'offchart'
            // Collect overlay indices for the group
            let indices = grp.map(ind => ind.index)
            this.$emit('legend-button-click', {
                button: 'group-settings',
                anchor: event.currentTarget.getBoundingClientRect().toJSON(),
                type: type,
                groupName: gname,
                dataIndices: indices
            })
        },
        close_popover() {
            this.popover.visible = false
        },
        on_rename(newName) {
            let oldName = this.popover.groupName
            let grp = this.indicators[oldName]
            if (grp) {
                for (let ind of grp) {
                    if (ind._ov && ind._ov.settings) {
                        this.$set(ind._ov.settings, 'group', newName)
                    }
                }
            }
            let ci = this.custom_groups.indexOf(oldName)
            if (ci >= 0) {
                this.$set(this.custom_groups, ci, newName)
            } else {
                this.custom_groups.push(newName)
            }
            this.save_groups()
            this.popover.groupName = newName
        },
        on_delete_group() {
            let gname = this.popover.groupName
            let grp = this.indicators[gname]
            if (!grp || !grp.length) {
                // Empty custom group — just remove it
                let ci = this.custom_groups.indexOf(gname)
                if (ci >= 0) { this.custom_groups.splice(ci, 1); this.save_groups() }
                this.close_popover()
                return
            }
            let indices = grp.map(ind => ind.index).sort((a, b) => b - a)
            let type = this.grid_id === 0 ? 'onchart' : 'offchart'
            for (let idx of indices) {
                this.$emit('legend-button-click', {
                    button: 'remove', type: type, dataIndex: idx
                })
            }
            let ci = this.custom_groups.indexOf(gname)
            if (ci >= 0) { this.custom_groups.splice(ci, 1); this.save_groups() }
            this.close_popover()
        },
        on_toggle_visibility() {
            let gname = this.popover.groupName
            let grp = this.indicators[gname]
            if (!grp) return
            let type = this.grid_id === 0 ? 'onchart' : 'offchart'
            const visible = !this.is_all_visible(gname)
            for (let ind of grp) {
                if (ind.v === visible) continue
                this.$emit('legend-button-click', {
                    button: 'display', type: type, dataIndex: ind.index
                })
            }
            this.popover.allVisible = !this.popover.allVisible
        },
        on_move_indicator({ indicator, ind, newGroup }) {
            ind = indicator || ind
            if (!ind) return
            if (ind._ov && ind._ov.settings) {
                this.$set(ind._ov.settings, 'group', newGroup)
            }
            if (!this.custom_groups.includes(newGroup)) {
                this.custom_groups.push(newGroup)
                this.save_groups()
            }
        },
        create_group() {
            let name = prompt('Enter group name:')
            if (!name || !name.trim()) return
            name = name.trim()
            if (!this.custom_groups.includes(name)) {
                this.custom_groups.push(name)
                this.save_groups()
            }
        },
        save_groups() {
            localStorage.setItem('tc_indicator_groups',
                JSON.stringify(this.custom_groups))
        },
        overlay_group_name(ov) {
            return ov ? ((ov.settings && ov.settings.group) || ov.group || 'Indicators') : 'Indicators'
        },
        has_valid_values(values) {
            return values && values.some(v => v && v.value && v.value !== '' && v.value !== 'n/a')
        },
        has_valid_group_values(grp) {
            return grp && grp.some(ind => {
                if (!ind.v || !ind.values) return false
                return ind.values.some(v => v && v.value && v.value !== '' && v.value !== 'n/a')
            })
        },
        reset_chart(event) {
            this.$emit('reset-chart', event)
        }
    }
}
</script>

<style>
.tc-legend {
    position: relative;
    z-index: 4;
    font-size: 1.25em;
    margin-left: 10px;
    pointer-events: none;
    text-align: left;
    user-select: none;
    font-weight: 300;
}
@media (min-resolution: 2x) {
    .tc-legend { font-weight: 500; }
}
.tc-ohlcv {
    pointer-events: none;
    margin-bottom: 0.5em;
}
.t-vue-lspan {
    font-variant-numeric: tabular-nums;
    font-weight: 300;
    color: #bbbbbb;
    margin-left: 0.1em;
    margin-right: 0.2em;
}
.t-vue-title {
    padding: 3px;
    margin-right: 0.25em;
    font-size: 1.05em;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    border: 1px solid transparent;
    pointer-events: none;
    cursor: inherit;
}
.t-vue-title:hover {
    border: 1px solid #d1d4dc;
    border-radius: 5px;
    background-color: rgba(255,255,255,0.2);
}
.t-vue-ohlcv {
    padding: 3px;
    font-size: 0.9em;
    font-weight: 400;
    border: 1px solid transparent;
    pointer-events: none;
    cursor: inherit;
}
.t-vue-ohlcv:hover {
    border: 1px solid #d1d4dc;
    border-radius: 5px;
    background-color: rgba(255,255,255,0.2);
}
/* Group header */
.t-vue-grp-header {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 2px;
    pointer-events: none;
    cursor: inherit;
}
.t-vue-grp-chevron {
    pointer-events: auto;
    background: transparent;
    border: 0;
    padding: 2px 4px;
    color: inherit;
    cursor: pointer;
    display: inline-block;
    font-size: 0.9em;
    color: #787b86;
    transition: transform 0.15s ease;
    transform: rotate(90deg);
    width: 14px;
    text-align: center;
}
.t-vue-grp-chevron.collapsed {
    transform: rotate(0deg);
}
.t-vue-grp {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    padding: 2px 6px;
    border: 1px solid transparent;
    border-radius: 4px;
    cursor: pointer;
}
.t-vue-grp:hover {
    border-color: #d1d4dc;
    background: rgba(255,255,255,0.15);
}
.t-vue-gname {
    font-weight: 600 !important;
    font-size: 0.85em !important;
}
.t-vue-grp-count {
    font-size: 0.7em;
    color: #787b86;
    font-weight: 400;
}
.t-vue-grp-values {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    margin-left: 8px;
    overflow: hidden;
    white-space: nowrap;
}
.t-vue-grp-actions {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin-left: 4px;
    pointer-events: none;
}
.t-vue-grp-header:hover .t-vue-grp-actions {
    display: inline-flex;
}
.t-vue-grp-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 20px;
    border: none;
    border-radius: 3px;
    background: transparent;
    color: #787b86;
    cursor: pointer;
    pointer-events: all;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1px;
    padding: 0;
    line-height: 1;
}
.t-vue-grp-btn:hover {
    background: rgba(0,0,0,0.08);
    color: #131722;
}
.t-vue-grp-del:hover {
    color: #e54150;
    background: rgba(229,65,80,0.08);
}
/* Indicator rows */
.t-vue-grp-ind {
    display: block;
    width: max-content;
    max-width: min(760px, calc(100vw - 72px));
    margin-bottom: 2px;
}
.t-vue-grp-ind > span {
    display: block;
}
.t-vue-ind {
    display: flex;
    align-items: center;
    gap: 4px;
    width: max-content;
    max-width: min(760px, calc(100vw - 72px));
    margin-left: 0.2em;
    margin-bottom: 0.34em;
    margin-top: 0.2em;
    padding: 2px 6px;
    font-size: 1em;
    border: 1px solid transparent;
    background: rgba(220,220,220,0.1);
    pointer-events: none;
    cursor: inherit;
    border-radius: 4px;
}
.t-vue-ind:hover {
    border: 1px solid #d1d4dc;
    background: rgba(255,255,255,0.15);
}
.t-vue-ind-main {
    display: inline-flex;
    align-items: baseline;
    gap: 12px;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
}
.t-vue-iname {
    min-width: 0;
    font-weight: 400 !important;
    font-size: 0.8em !important;
    line-height: 1.15;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.t-vue-iname-inactive {
    color: #d1d4dc;
    filter: invert(40%);
}
.t-vue-ivalues {
    display: inline-flex;
    align-items: baseline;
    gap: 10px;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
}
.t-vue-ivalue {
    margin-left: 0;
    margin-right: 0;
    font-size: 0.74em;
    font-weight: 500;
    line-height: 1.1;
    color: #aeb5c2;
    white-space: nowrap;
    letter-spacing: 0;
}
.t-vue-ind-actions {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    opacity: 1;
    visibility: visible;
    pointer-events: none;
}
.t-vue-unknown { color: #999; }
/* New group button */
.t-vue-new-grp {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    margin-top: 4px;
    font-size: 0.72em;
    color: #787b86;
    cursor: pointer;
    pointer-events: all;
    border-radius: 4px;
    border: 1px dashed transparent;
    transition: all 0.15s;
}
.t-vue-new-grp:hover {
    color: #2962ff;
    border-color: #2962ff;
    background: rgba(41,98,255,0.04);
}
/* Transitions */
.tcjs-appear-enter-active,
.tcjs-appear-leave-active { transition: all .25s ease; }
.tcjs-appear-enter, .tcjs-appear-leave-to { opacity: 0; }

/* Dark theme legend styles */
.chart-page-dark .t-vue-title:hover {
    border-color: #5b9cf6;
    background-color: rgba(91, 156, 246, 0.1);
}
.chart-page-dark .t-vue-ohlcv:hover {
    border-color: #5b9cf6;
    background-color: rgba(91, 156, 246, 0.1);
}
.chart-page-dark .t-vue-grp-chevron {
    pointer-events: auto;
    background: transparent;
    border: 0;
    padding: 2px 4px;
    color: inherit;
    cursor: pointer;
    color: #9ca3af;
}
.chart-page-dark .t-vue-grp:hover {
    border-color: #5b9cf6;
    background: rgba(255, 255, 255, 0.05);
}
.chart-page-dark .t-vue-grp-count {
    color: #9ca3af;
}
.chart-page-dark .t-vue-grp-btn {
    color: #9ca3af;
}
.chart-page-dark .t-vue-grp-btn:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #d1d4dc;
}
.chart-page-dark .t-vue-grp-del:hover {
    color: #ef5350;
    background: rgba(239, 83, 80, 0.1);
}
.chart-page-dark .t-vue-ind {
    background: rgba(255, 255, 255, 0.05);
}
.chart-page-dark .t-vue-ind:hover {
    border-color: #5b9cf6;
    background: rgba(255, 255, 255, 0.1);
}
.chart-page-dark .t-vue-iname-inactive {
    color: #6b7280;
    filter: none;
}
.chart-page-dark .t-vue-unknown {
    color: #6b7280;
}
.chart-page-dark .t-vue-new-grp {
    color: #9ca3af;
    border-color: rgba(155, 163, 175, 0.3);
}
.chart-page-dark .t-vue-new-grp:hover {
    color: #5b9cf6;
    border-color: #5b9cf6;
    background: rgba(91, 156, 246, 0.1);
}
</style>

<style>
.tc-ohlcv .t-vue-ohlcv{opacity:1;color:inherit;font-weight:500}.tc-candle-change{color:#2864F0;margin:0 8px;font-weight:700}.tc-ohlcv button{pointer-events:all;border:0;background:transparent;color:inherit;cursor:pointer}
</style>
